import { ArrowDropDownCircleOutlined, Close, Download } from '@mui/icons-material';
import {
  Alert,
  Button,
  Typography,
  Theme,
  Grid,
  Box,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
  ButtonGroup
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, Formik, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useQuery } from 'react-query';
import {
  getPlanStyles,
  getCladdingVersions,
  getAgentsSimple,
  getPlanSeriesBrochures,
  createPlanSeriesBrochures
} from '../../../fetch';
import {
  IDropdownResponse,
  IPlanStyleRes,
  IPeopleSimple,
  IResponse,
  IPlanSeriesBrochures
} from '../../../models';
import { downloadFile, base64StringToBlob } from '../../../helpers';
import { Page, PageTitleWithBack, SMBSelect } from '../../../components';

const brochureSchema = Yup.object().shape({
  planId: Yup.number(),
  planName: Yup.string(),
  isActive: Yup.boolean(),
  elevations: Yup.array(),
  includeThreeCar: Yup.boolean(),
  elevation: Yup.string()
});

const formSchema = Yup.object().shape({
  elevation: Yup.array().of(brochureSchema)
});

export const PlanBrochures = () => {
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [planStyleId, setPlanStyleId] = useState<number | string>('');
  const [selectedPlanStyle, setSelectedPlanStyle] = useState<string | number>('');
  const [selectedCladdingVersion, setSelectedCladdingVersion] = useState<string>('AllBrick');
  const [selectedAgent, setSelectedAgent] = useState<number | string>('');
  const [isGeneratingPdf, setGeneratingPdf] = useState<boolean>(false);
  const [brochuresGeneratedCount, setBrochuresGeneratedCount] = useState<number | string>('');
  const [brochuresGeneratedMissingImagesCount, setBrochuresGeneratedMissingImagesCount] = useState<
    number | null
  >(null);
  const [brochuresExcludedCount, setBrochuresExcludedCount] = useState<number | null>(null);
  const [brochuresFailedCount, setBrochuresFailedCount] = useState<number | null>(null);
  const [showFilesGeneratedDetails, setFilesGeneratedDetails] = useState<boolean>(false);
  const [fileDownloadName, setFileDownloadName] = useState<string>();
  const [fileContents, setFileContents] = useState<string>();

  const { isLoading: isLoadingPlanStyles, data: planStyles } = useQuery<IPlanStyleRes, Error>(
    ['planStyles'],
    () => getPlanStyles()
  );

  const { isLoading: isLoadingCladdingVersions, data: claddingVersions } = useQuery<
    IDropdownResponse[],
    Error
  >(['claddingVersions'], () => getCladdingVersions());

  const { isLoading: isLoadingAgents, data: agentsData } = useQuery<
    IResponse<IPeopleSimple[]>,
    Error
  >(['agents'], () => getAgentsSimple());

  const {
    isLoading: isLoadingBrochureOptions,
    data: brochureOptions,
    refetch: refetchBrochureOptions
  } = useQuery<IPlanSeriesBrochures[], Error>(
    ['brochureOptions', planStyleId],
    () => getPlanSeriesBrochures(planStyleId),
    {
      enabled: !!planStyleId
    }
  );

  const handleDownloadPDF = async () => {
    return downloadFile(base64StringToBlob(fileContents), fileDownloadName);
  };

  const initialBrochureValues = brochureOptions?.map(obj => {
    const bro: any = {};
    bro.planId = obj?.planId;
    bro.planName = obj?.planName ?? '';
    bro.isActive = obj?.isActive ?? false;
    bro.elevations = obj?.elevations ?? [];
    bro.includeThreeCar = false;

    const hasSkip = bro.elevations.find(item => item.value === 'skip');
    if (!hasSkip) {
      bro.elevations.unshift({ value: 'skip', description: 'Skip', shorthand: null });
    }
    bro.elevation =
      obj?.isActive && obj?.elevations?.length > 1 ? obj?.elevations[1]?.value : 'skip';
    return bro;
  });

  return (
    <Page title="Print Full Plan Series Brochures">
      <PageTitleWithBack
        backText="Back to Home"
        backUrl={'/development'}
        title="Plan Brochures"
        subTitle="This page allows you to print a full set of brochures for a given plan series."
      />
      <Box mt={0.5} mb={0.5}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <SMBSelect
              disabled={isLoadingPlanStyles}
              name={`planStyles`}
              label="Plan Series"
              id={`planStyles`}
              variant="outlined"
              required
              value={selectedPlanStyle || ''}
              size="small"
              onChange={e => {
                setPlanStyleId(Number(e.target.value));
                if (planStyleId) {
                  refetchBrochureOptions();
                }
              }}
            >
              {!isLoadingPlanStyles &&
                planStyles?.records.map((record, index) => {
                  return (
                    <MenuItem
                      key={record.planStyleId}
                      value={record.planStyleId}
                      onClick={() => {
                        setSelectedPlanStyle(record.planStyleId);
                        setPlanStyleId(record.planStyleId);
                      }}
                    >
                      {record.name}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SMBSelect
              disabled={isLoadingCladdingVersions}
              name={`getCladding`}
              label="Cladding"
              variant="outlined"
              required
              id={`getCladding`}
              value={selectedCladdingVersion}
              size="small"
              onChange={e => {
                setSelectedCladdingVersion(String(e.target.value));
              }}
            >
              {!isLoadingCladdingVersions &&
                claddingVersions?.map((cladding, index) => {
                  return (
                    <MenuItem
                      key={`${index}`}
                      value={cladding.value}
                      onClick={() => {
                        setSelectedCladdingVersion(cladding.value);
                      }}
                    >
                      {cladding.description}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SMBSelect
              disabled={isLoadingAgents}
              name={`agents`}
              label={`Agent`}
              variant="outlined"
              handleClear={() => setSelectedAgent('')}
              value={selectedAgent || ''}
              size="small"
              onChange={e => {
                setSelectedAgent(Number(e.target.value));
              }}
            >
              {!isLoadingAgents &&
                agentsData?.records.map((record, index) => {
                  return (
                    <MenuItem
                      key={`${index}`}
                      value={record.personId}
                      onClick={() => {
                        setSelectedAgent(record.personId);
                      }}
                    >
                      {record.lastName}, {record.firstName}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
        </Grid>
      </Box>
      {!isLoadingBrochureOptions && brochureOptions?.length === 0 && (
        <Box mt={1}>
          <Alert severity="info">
            This Plan Series does not contain Plans, please select another option.
          </Alert>
        </Box>
      )}
      {!isLoadingBrochureOptions && brochureOptions?.length > 0 && (
        <Box mt={1}>
          <Typography className={classes.subsectionTitle}>Select the Plans to include</Typography>
          <Typography className={classes.subtitle}>
            To include a Plan in the Full Set select the elevation you want, otherwise choose skip.
          </Typography>
          <Alert severity="warning">
            If no Elevations are available, the menu will reflect "Skip" and the Plan will not be
            included in the Brochure.
          </Alert>

          {!isLoadingBrochureOptions && (
            <Formik
              enableReinitialize={true}
              initialValues={{
                brochureOptions: initialBrochureValues
              }}
              validationSchema={formSchema}
              onSubmit={async (values, actions) => {
                try {
                  setGeneratingPdf(true);
                  const data: any = {
                    ...values
                  };
                  const allPlanSelections = data.brochureOptions.map(obj => {
                    const plan: any = {};
                    plan.planId = obj?.planId;
                    plan.elevationId = Number(obj?.elevation) ?? 0;
                    plan.includeThreeCar = obj?.includeThreeCar;

                    return plan;
                  });

                  const payload = {
                    cladding: selectedCladdingVersion ?? 'AllBrick',
                    agentId: selectedAgent ?? 0,
                    planSelections: allPlanSelections
                  };

                  const pdfInfo = await createPlanSeriesBrochures(payload);
                  setBrochuresGeneratedCount(pdfInfo.brochuresGeneratedCount);
                  setBrochuresGeneratedMissingImagesCount(
                    pdfInfo.brochuresGeneratedMissingImagesCount
                  );
                  setBrochuresExcludedCount(pdfInfo.brochuresExcludedCount);
                  setBrochuresFailedCount(pdfInfo.brochuresFailedCount);
                  setFileContents(pdfInfo.fileContents);
                  setFileDownloadName(pdfInfo.fileDownloadName);
                } catch (error) {
                  enqueueSnackbar(
                    error?.Detail ?? 'Something went wrong. Plan series brochure was not created.',
                    {
                      variant: 'error'
                    }
                  );
                } finally {
                  setGeneratingPdf(false);
                  setFilesGeneratedDetails(true);
                }
              }}
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                handleSubmit,
                dirty,
                isValid,
                handleBlur
              }) => {
                return (
                  <>
                    <Form onSubmit={handleSubmit}>
                      <Box mt={1}>
                        <FieldArray
                          name="brochureOptions"
                          render={arrayHelpers => (
                            <div>
                              {values.brochureOptions.map((brochure, i) => (
                                <Box key={`brochureOptions.${i}`}>
                                  <Box mt={1} mb={0.5}>
                                    <Divider />
                                  </Box>
                                  <Grid container spacing={1} ml={2}>
                                    <Grid item xs={12}>
                                      <Grid
                                        container
                                        alignItems="center"
                                        justifyContent="center"
                                        spacing={1}
                                      >
                                        <Grid item xs={12} sm={3}>
                                          <Typography>{brochure.planName}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <Field name={`brochureOptions.${i}.elevation`}>
                                            {({ field }) => {
                                              return (
                                                <SMBSelect
                                                  disabled={
                                                    isLoadingBrochureOptions ||
                                                    Boolean(fileContents)
                                                  }
                                                  sx={{ width: '20rem' }}
                                                  name={field.value}
                                                  variant="outlined"
                                                  value={field.value}
                                                  size="small"
                                                  onChange={e => {
                                                    setFieldValue(
                                                      `brochureOptions.${i}.elevation`,
                                                      e.target.value
                                                    );
                                                  }}
                                                >
                                                  {brochureOptions[i].elevations?.map(
                                                    (elevation, index) => {
                                                      return (
                                                        <MenuItem
                                                          key={`brochureOptions.${index}.elevations`}
                                                          value={elevation?.value}
                                                        >
                                                          {elevation?.description}
                                                        </MenuItem>
                                                      );
                                                    }
                                                  )}
                                                </SMBSelect>
                                              );
                                            }}
                                          </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                          <FormControlLabel
                                            color="primary"
                                            control={
                                              <Checkbox
                                                disabled={
                                                  isLoadingBrochureOptions || Boolean(fileContents)
                                                }
                                                checked={values.brochureOptions[i].includeThreeCar}
                                                id="isActive"
                                                onChange={e =>
                                                  setFieldValue(
                                                    `brochureOptions[${i}].includeThreeCar`,
                                                    !values.brochureOptions[i].includeThreeCar
                                                  )
                                                }
                                              />
                                            }
                                            label="Include 3 Car"
                                          />
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                            </div>
                          )}
                        />
                      </Box>
                      <Box mt={2} mb={1}>
                        <Divider />
                        {showFilesGeneratedDetails && !isGeneratingPdf && (
                          <Typography className={classes.detailMessage}>
                            {brochuresGeneratedCount} brochures successfully generated,{' '}
                            {brochuresGeneratedMissingImagesCount} brochures missing images,{' '}
                            {brochuresExcludedCount} brochures excluded, {brochuresFailedCount}{' '}
                            brochures failed
                          </Typography>
                        )}
                      </Box>
                      <ButtonGroup>
                        {fileContents && !isGeneratingPdf && (
                          <Button
                            startIcon={<Download />}
                            variant="contained"
                            color="primary"
                            onClick={() => handleDownloadPDF()}
                          >
                            Download
                          </Button>
                        )}
                        {!fileContents && (
                          <LoadingButton
                            disabled={
                              !selectedPlanStyle ||
                              isSubmitting ||
                              !isValid ||
                              initialBrochureValues.length === 0
                            }
                            loading={isGeneratingPdf}
                            loadingPosition="start"
                            variant="contained"
                            color="primary"
                            startIcon={<ArrowDropDownCircleOutlined />}
                            type="submit"
                          >
                            Generate File
                          </LoadingButton>
                        )}
                        <Button
                          type="button"
                          variant="contained"
                          color="inherit"
                          startIcon={<Close />}
                          onClick={() => {
                            history.push('/development/plans/manage-plans');
                          }}
                        >
                          Cancel
                        </Button>
                        {fileContents && (
                          <div className={classes.resetButton}>
                            <Button
                              variant="contained"
                              type="button"
                              color="inherit"
                              startIcon={<Close />}
                              onClick={() => {
                                setSelectedPlanStyle('');
                                setSelectedAgent(null);
                                setPlanStyleId(null);
                                setFileContents('');
                                setBrochuresGeneratedCount(null);
                                setBrochuresGeneratedMissingImagesCount(null);
                                setBrochuresExcludedCount(null);
                                setBrochuresFailedCount(null);
                                setFilesGeneratedDetails(false);
                              }}
                            >
                              Reset
                            </Button>
                          </div>
                        )}
                      </ButtonGroup>
                    </Form>
                  </>
                );
              }}
            </Formik>
          )}
        </Box>
      )}
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  selectWrapper: {
    width: '50%',
    marginBottom: '12px',
    '@media (max-width: 768px)': {
      width: '100%'
    }
  },
  title: {
    fontSize: 20,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  },
  subsectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.25)
  },
  subtitle: {
    fontSize: 14,
    paddingBottom: theme.spacing(1)
  },
  container: {
    padding: theme.spacing(0.5)
  },
  detailMessage: {
    paddingTop: theme.spacing(1)
  },
  resetButton: {
    marginLeft: theme.spacing(2)
  }
}));
